import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType, TransactionsLists } from 'vopay';
import useFetchACHWithdrawTransaction from '@/hooks/useApi/useFetchACHWithdrawTransaction';
import AchWithdrawalTransactionInfo from './ACHWithdrawalTransactionInfo';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type AchWithdrawalTransactionProps = {
  TransactionID: string;
  // Resolved by TransactionDetails.tsx via a generic lookup by ID — shared with FundingStatusBanner
  // instead of this container fetching its own copy for the Credit Card fallback below.
  parentTransaction?: TransactionsLists;
} & Omit<
  ComponentProps<typeof AchWithdrawalTransactionInfo>,
  'transaction' | 'parentTransactionId' | 'parentTransactionStatus' | 'parentType'
>;

export default function AchWithdrawalTransactionContainer({
  TransactionID,
  Notes,
  isParent = false,
  jwt,
  ELinxRequestID = '',
  parentTransaction,
  ...props
}: AchWithdrawalTransactionProps) {
  const { userSettings } = useUserSettingsProvider();
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { achWithdrawTransaction, fetchAchWithdrawTransaction, resetAchWithdrawTransaction } =
    useFetchACHWithdrawTransaction({ bearer: jwt });

  useEffect(() => {
    blockingLoader.show();
    resetAchWithdrawTransaction();

    fetchAchWithdrawTransaction({ TransactionID, Timezone: userSettings.timezone })
      .catch((error) => {
        // Not returned: the dialog's promise settles on dismissal, and the loader must drop before that.
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred'));
      })
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = achWithdrawTransaction;

  const fundingParentTransaction = Object.values(transaction?.ParentTransaction || {}).find(
    (parent) => parent.TransactionType === TransactionType.ACH_FUNDING,
  );

  // The ACH Funding join above doesn't return a Credit Card parent (Credit Card funding isn't in
  // the table that join relies on), so fall back to the generically-resolved parent for that case only.
  const creditCardParent =
    !fundingParentTransaction && parentTransaction?.TransactionType === TransactionType.CREDIT_CARD
      ? parentTransaction
      : undefined;

  return (
    <div>
      {transaction && (
        <AchWithdrawalTransactionInfo
          transaction={transaction}
          Notes={Notes}
          isParent={isParent}
          jwt={jwt}
          ELinxRequestID={ELinxRequestID}
          parentTransactionId={fundingParentTransaction?.TransactionID || creditCardParent?.TransactionID}
          parentTransactionStatus={fundingParentTransaction?.TransactionStatus || creditCardParent?.TransactionStatus}
          parentType={fundingParentTransaction ? 'ACH Funding' : creditCardParent ? 'Credit Card' : undefined}
          {...props}
        />
      )}
    </div>
  );
}
